import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Form,
  OverlayTrigger,
  Spinner,
  Tooltip,
} from "react-bootstrap";
import { VscListFlat } from "react-icons/vsc";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { checkAuthAndRedirect } from "../../../utils/auth";
import authService from "../../../utils/authService";
import QuestionPreviewModal from "./preview/QuestionPreviewModal";
import type { PreviewQuestion } from "./preview/questionPreviewAdapter";
import { TEST_FLOW_API } from "./testApiPaths";
import TestPageShell from "./TestPageShell";
import { decodeTestNameFromPath, useTestRoutes } from "./testRoutes";
import "./Test.css";

type SameTestSlotUsage = {
  slot_id: number;
  slot_label: string;
};

type UsedInTestUsage = {
  test_id: string;
  test_name: string;
  used_at?: string | null;
};

type AlternativeQuestion = {
  question_id: string;
  sort_order?: number;
  is_reused?: boolean;
  question_type?: string;
  question_types_enum?: string | null;
  level?: string;
  subject_id?: string;
  topic_id?: string;
  sub_topic_id?: string;
  subject_name?: string;
  topic_name?: string;
  sub_topic_name?: string;
  question_data?: Record<string, unknown> | null;
  same_test_slots?: SameTestSlotUsage[];
  used_in_tests?: UsedInTestUsage[];
};

const questionText = (q: AlternativeQuestion) => {
  const data = q.question_data;
  if (!data || typeof data !== "object") return q.question_id;
  return String(data.Qn || data.question || data.text || q.question_id);
};

const toPreview = (q: AlternativeQuestion): PreviewQuestion => ({
  question_id: q.question_id,
  question_type: q.question_type,
  question_types_enum: q.question_types_enum,
  level: q.level,
  subject_name: q.subject_name,
  topic_name: q.topic_name,
  sub_topic_name: q.sub_topic_name,
  question_data: q.question_data || null,
});

const formatUsedAt = (value?: string | null) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
};

const slotHoverLines = (slots: SameTestSlotUsage[] = []) =>
  slots.map((s, i) => `${i + 1}. ${s.slot_label}`).join("\n");

const usedHoverLines = (tests: UsedInTestUsage[] = []) =>
  tests
    .map((t, i) => {
      const when = formatUsedAt(t.used_at);
      return when
        ? `${i + 1}. ${t.test_name} - ${when}`
        : `${i + 1}. ${t.test_name}`;
    })
    .join("\n");

const QuestionReplacePicker: React.FC = () => {
  const { testName: testNameParam } = useParams<{ testName: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const routes = useTestRoutes();
  const routeTestName = decodeTestNameFromPath(testNameParam);

  const testId = searchParams.get("test_id") || "";
  const slotId = searchParams.get("slot_id") || "";
  const questionId = searchParams.get("question_id") || "";
  const headerSubject = searchParams.get("subject_name") || "Subject";
  const headerTopic = searchParams.get("topic_name") || "Topic";
  const headerSubtopic = searchParams.get("sub_topic_name") || "Subtopic";

  const [loading, setLoading] = useState(true);
  const [replacing, setReplacing] = useState(false);
  const [error, setError] = useState("");
  const [alternatives, setAlternatives] = useState<AlternativeQuestion[]>([]);
  const [meta, setMeta] = useState<{
    subject_name?: string;
    topic_name?: string;
    sub_topic_name?: string;
    level?: string;
    question_type?: string;
  }>({});
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [previewQuestion, setPreviewQuestion] =
    useState<PreviewQuestion | null>(null);

  const backToReview = () => {
    if (!routeTestName || !testId) {
      navigate(routes.configure, { replace: true });
      return;
    }
    navigate(
      routes.configureDetail(routeTestName, testId, {
        reopen_slot: slotId,
      }),
      { replace: true }
    );
  };

  useEffect(() => {
    checkAuthAndRedirect();
  }, []);

  useEffect(() => {
    if (!routeTestName || !testId || !slotId || !questionId) {
      navigate(routes.configure, { replace: true });
    }
  }, [routeTestName, testId, slotId, questionId, navigate, routes.configure]);

  useEffect(() => {
    if (!testId || !slotId || !questionId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await authService.get(
          `${TEST_FLOW_API}/tests/${testId}/slots/${slotId}/questions/${encodeURIComponent(
            questionId
          )}/alternatives`
        );
        if (cancelled) return;
        const list: AlternativeQuestion[] = res.data?.alternatives || [];
        setAlternatives(list);
        // Pre-select the question being replaced when it is in the list.
        const currentStillListed = list.some((q) => q.question_id === questionId);
        setSelectedId(currentStillListed ? questionId : null);
        setMeta({
          subject_name: res.data?.subject_name,
          topic_name: res.data?.topic_name,
          sub_topic_name: res.data?.sub_topic_name,
          level: res.data?.level,
          question_type: res.data?.question_type,
        });
      } catch (err: unknown) {
        console.error(err);
        if (!cancelled) {
          const detail =
            (err as { response?: { data?: { detail?: string } } })?.response
              ?.data?.detail || "Failed to load alternative questions.";
          setError(String(detail));
          setAlternatives([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [testId, slotId, questionId]);

  const subjectLabel = meta.subject_name || headerSubject;
  const topicLabel = meta.topic_name || headerTopic;
  const subtopicLabel = meta.sub_topic_name || headerSubtopic;

  const selected = useMemo(
    () => alternatives.find((q) => q.question_id === selectedId) || null,
    [alternatives, selectedId]
  );

  const confirmReplace = async () => {
    if (!selectedId || !testId || !slotId || !questionId) return;
    setReplacing(true);
    setError("");
    try {
      await authService.post(
        `${TEST_FLOW_API}/tests/${testId}/slots/${slotId}/replace`,
        {
          question_id: questionId,
          replacement_question_id: selectedId,
        }
      );
      backToReview();
    } catch (err: unknown) {
      console.error(err);
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "Failed to replace question.";
      setError(String(detail));
      setReplacing(false);
    }
  };

  if (!routeTestName || !testId || !slotId || !questionId) {
    return null;
  }

  return (
    <TestPageShell showCardSearch={false}>
      <div
        className="border rounded bg-white my-2 me-2 d-flex flex-column"
        style={{ height: "calc(100vh - 160px)" }}
      >
        <div className="border-bottom p-3 d-flex flex-wrap justify-content-between align-items-start gap-2">
          <div>
            <div className="fw-semibold mb-1">
              <VscListFlat className="me-2" />
            {subjectLabel}
            <span className="text-muted mx-2">/</span>
            {topicLabel}
            <span className="text-muted mx-2">/</span>
            {subtopicLabel}
            
            {meta.level ? (
                <>
                  <Badge bg="secondary" className="mx-2">
                    {String(meta.level).replace("level", "L")}
                  </Badge>
                </>
              ) : null}
              {meta.question_type ? (
                <>
                  {" "}
                  <Badge bg="info">{meta.question_type}</Badge>
                </>
              ) : null}
            </div>
          </div>
          <div className="d-flex gap-2">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={backToReview}
              disabled={replacing}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={!selectedId || replacing || loading}
              onClick={() => void confirmReplace()}
            >
              {replacing ? (
                <Spinner animation="border" size="sm" className="me-1" />
              ) : null}
              Replace
            </Button>
          </div>
        </div>

        <div className="flex-grow-1 overflow-y-auto p-3" style={{ minHeight: 0 }}>
          {error && (
            <Alert variant="danger" className="py-2 small">
              {error}
            </Alert>
          )}

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" size="sm" />
            </div>
          ) : alternatives.length === 0 ? (
            <div className="text-muted text-center py-5">
              No alternative questions available for this subtopic, level, and
              type.
            </div>
          ) : (
            alternatives.map((question, index) => {
              const checked = selectedId === question.question_id;
              const isCurrent = question.question_id === questionId;
              const usedCount = question.used_in_tests?.length || 0;
              const sameSlots = question.same_test_slots?.length || 0;
              const text = questionText(question);
              return (
                <div
                  key={question.question_id}
                  className={`card shadow-sm mb-3 ${
                    checked ? "border-primary" : ""
                  }`}
                >
                  <div className="card-body py-2">
                    <div className="d-flex align-items-start gap-2">
                      <Form.Check
                        type="radio"
                        name="replacement-question"
                        id={`alt-${question.question_id}`}
                        className="mt-1 flex-shrink-0"
                        checked={checked}
                        onChange={() => setSelectedId(question.question_id)}
                        label=""
                      />
                      <div className="flex-grow-1 d-flex align-items-start gap-2 min-w-0">
                        <button
                          type="button"
                          className="btn btn-link p-0 text-start text-decoration-none text-break flex-grow-1 min-w-0"
                          title="Preview question"
                          onClick={() =>
                            setPreviewQuestion(toPreview(question))
                          }
                        >
                          <span className="fw-semibold text-body me-1">
                            {index + 1}.
                          </span>
                          <span className="text-primary">
                            {text.length > 220
                              ? `${text.slice(0, 220)}…`
                              : text}
                          </span>
                        </button>
                        <div className="d-flex flex-wrap align-items-center gap-1 flex-shrink-0 ms-auto">
                          {isCurrent && (
                            <Badge bg="primary">Current</Badge>
                          )}
                          {sameSlots > 0 && (
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip
                                  id={`slot-tip-${question.question_id}`}
                                >
                                  <div
                                    style={{
                                      whiteSpace: "pre-line",
                                      textAlign: "left",
                                    }}
                                  >
                                    {slotHoverLines(question.same_test_slots)}
                                  </div>
                                </Tooltip>
                              }
                            >
                              <Badge
                                bg="warning"
                                text="dark"
                                style={{ cursor: "help" }}
                              >
                                Slot
                              </Badge>
                            </OverlayTrigger>
                          )}
                          {usedCount > 0 && (
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip
                                  id={`used-tip-${question.question_id}`}
                                >
                                  <div
                                    style={{
                                      whiteSpace: "pre-line",
                                      textAlign: "left",
                                    }}
                                  >
                                    {usedHoverLines(question.used_in_tests)}
                                  </div>
                                </Tooltip>
                              }
                            >
                              <Badge
                                bg="secondary"
                                style={{ cursor: "help" }}
                              >
                                Used
                              </Badge>
                            </OverlayTrigger>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <QuestionPreviewModal
        question={previewQuestion}
        onHide={() => setPreviewQuestion(null)}
      />
    </TestPageShell>
  );
};

export default QuestionReplacePicker;
